import { AUTO_NUMBERING_CONFIG_TYPE } from '@govinity/shared-config';
import { z } from 'zod';
export const autoNumberingConfigTypeSchema = z.enum([
    AUTO_NUMBERING_CONFIG_TYPE.LEGISLATIVE_ITEM,
    AUTO_NUMBERING_CONFIG_TYPE.DOCUMENT,
    AUTO_NUMBERING_CONFIG_TYPE.SEAT_APPLICATION,
]);
const ALLOWED_VARIABLES_BY_TYPE = {
    LEGISLATIVE_ITEM: ['[YEAR]', '[COUNTER]', '[ITEM_TYPE_CODE]', '[DEPARTMENT_CODE]'],
    DOCUMENT: ['[YEAR]', '[COUNTER]', '[DOC_TYPE_CODE]', '[DEPARTMENT_CODE]'],
    SEAT_APPLICATION: ['[YEAR]', '[COUNTER]'],
};
// Auto-numbering variables schema (internal use - includes year and counter)
export const autoNumberingVariablesSchema = z.object({
    year: z.number().optional(),
    counter: z.number().optional(),
    typeCode: z.string().optional(),
    docTypeCode: z.string().optional(),
    departmentCode: z.string().optional(),
});
// Additional variables schema (external use - only user-provided variables)
export const autoNumberingAdditionalVariablesSchema = z.object({
    typeCode: z.string().optional(),
    docTypeCode: z.string().optional(),
    departmentCode: z.string().optional(),
});
const formulaBaseSchema = z
    .string()
    .min(1, 'Formula cannot be empty')
    .refine((formula) => formula.trim().length > 0, 'Formula cannot be empty')
    .refine((formula) => formula.length >= 3, 'Formula must be at least 3 characters long')
    .refine((formula) => formula.length <= 200, 'Formula cannot exceed 200 characters')
    .refine((formula) => !/[^A-Za-z0-9\[\]_-]/.test(formula), 'Formula contains invalid characters. Only letters, numbers, brackets, hyphens, and underscores are allowed')
    .refine((formula) => {
    const openBrackets = (formula.match(/\[/g) || []).length;
    const closeBrackets = (formula.match(/\]/g) || []).length;
    return openBrackets === closeBrackets;
}, 'Formula has unbalanced brackets')
    .refine((formula) => formula.includes('[COUNTER]'), 'Formula must include [COUNTER] variable')
    .refine((formula) => !formula.includes('[[') && !formula.includes(']]'), 'Formula contains consecutive brackets which is invalid syntax')
    .refine((formula) => !formula.includes('[]'), 'Formula contains empty brackets which is invalid syntax');
// Validates that every `[VARIABLE]` token in the formula is allowed for the given type.
const refineFormulaForType = (value, ctx) => {
    const allowed = ALLOWED_VARIABLES_BY_TYPE[value.type];
    const matches = value.formula.match(/\[([A-Z_]+)\]/g) || [];
    for (const match of matches) {
        if (!allowed.includes(match)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['formula'],
                message: `Formula variable ${match} is not allowed for type ${value.type}. Allowed: ${allowed.join(', ')}`,
            });
            return;
        }
    }
};
// Base schema for auto numbering config
export const autoNumberingConfigBaseSchema = z.object({
    name: z.string().min(1, 'Configuration name is required').max(100, 'Name too long'),
    description: z.string().optional().nullable(),
    type: autoNumberingConfigTypeSchema,
    startingCounter: z.number().min(1, 'Starting counter must be at least 1'),
    restartYearly: z.boolean(),
    counterPadding: z
        .number()
        .min(1, 'Counter padding must be at least 1')
        .max(10, 'Counter padding cannot exceed 10 digits')
        .default(3),
    formula: formulaBaseSchema,
    isActive: z.boolean(),
});
// Create schema
export const createAutoNumberingConfigSchema = autoNumberingConfigBaseSchema.superRefine(refineFormulaForType);
// Update schema — `type` is immutable post-create, so it's omitted entirely.
// Formula validation against the existing config's type happens in the controller
// using `createAutoNumberingConfigSchema`.
export const updateAutoNumberingConfigSchema = autoNumberingConfigBaseSchema
    .omit({ type: true })
    .partial();
// Legislative type reference schema
export const legislativeTypeReferenceSchema = z.object({
    id: z.number(),
    name: z.string(),
    code: z.string(),
    status: z.string(),
});
// Response schema (uses the raw object — refinements aren't useful on responses)
export const autoNumberingConfigResponseSchema = autoNumberingConfigBaseSchema.extend({
    id: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
    governmentId: z.number(),
    lastUsedDate: z.string().nullable(),
    lastGeneratedNumber: z.string().nullable(),
    currentCounter: z.number(),
    currentYear: z.number().nullable(),
    usageCount: z.number(),
    legislativeTypes: z.array(legislativeTypeReferenceSchema),
    preview: z.string(),
});
// Preview request schema
export const previewFormulaSchema = z.object({
    formula: z.string().min(1, 'Formula is required'),
    type: autoNumberingConfigTypeSchema.optional(),
    variables: autoNumberingAdditionalVariablesSchema.optional(),
});
// Preview response schema
export const previewFormulaResponseSchema = z.object({
    preview: z.string(),
    variables: autoNumberingVariablesSchema,
});
// Usage statistics schema
export const usageStatisticsSchema = z.object({
    totalUsage: z.number(),
    lastUsedDate: z.string().nullable(),
    lastGeneratedNumber: z.string().nullable(),
    currentCounter: z.number(),
    currentYear: z.number().nullable(),
    recentUsages: z.array(z.object({
        id: z.number(),
        entityType: z.string(),
        entityId: z.number(),
        generatedNumber: z.string(),
        generatedAt: z.string(),
    })),
});
// Generate number request schema
export const generateNumberSchema = z.object({
    entityType: z.string().min(1, 'Entity type is required'),
    entityId: z.number().min(1, 'Entity ID is required'),
    variables: autoNumberingAdditionalVariablesSchema.optional(),
});
// Generate number response schema
export const generateNumberResponseSchema = z.object({
    generatedNumber: z.string(),
});
